/**
 * ConversationFunnelEmbed - Production Embeddable Page
 * 
 * Designed for script/iframe embedding on landing pages.
 * Supports GHL, WordPress, Webflow, and custom pages.
 * 
 * URL Parameters:
 * - tenant_id (required): Tenant identifier
 * - funnel (required): Funnel slug
 * - embed (optional): 'true' if loaded via embed.js
 * - embed_id (optional): Unique embed instance ID
 * - trustedform_cert_url (optional): TrustedForm cert from parent page
 * - page_url (optional): Parent page URL
 * - referrer_url (optional): Referrer URL
 * - prefill_* (optional): Prefill contact fields
 * - utm_* (optional): Attribution parameters
 * 
 * TrustedForm Runtime (MA-OPS-061):
 * - Loads TrustedForm inside widget when funnel settings enable it
 * - Captures certificate for leads submitted within the iframe
 * - Supports fallback to parent page certificate
 * 
 * @version 1.2.0
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConversationFunnel from '@/components/ConversationFunnel';
import { useTrustedFormRuntime } from '@/hooks/useTrustedFormRuntime';
import { deriveBrandFromParams } from '@/lib/brandTokens';

// ==========================================================================
// Helpers
// ==========================================================================

const isEmbedMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('embed') === 'true' || window.parent !== window;
};

const getEmbedId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('embed_id') || null;
};

const postToParent = (type, payload = {}) => {
  if (window.parent !== window) {
    try {
      const embedId = getEmbedId();
      window.parent.postMessage({ 
        type, 
        payload,
        embedId 
      }, '*');
    } catch (e) {
      console.warn('[MissionAssist] Failed to post message:', e);
    }
  }
};

// ==========================================================================
// Component
// ==========================================================================

export default function ConversationFunnelEmbed() {
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const lastHeightRef = useRef(0);
  const heightCheckIntervalRef = useRef(null);
  
  const isEmbed = isEmbedMode();
  const embedId = getEmbedId();
  
  // PostHog Analytics for Landing Pages
  // Track visitor behavior on user-generated landing pages (Meta ads, profit paths, etc.)
  useEffect(() => {
    // Only load PostHog if not already loaded
    if (window.posthog) return;
    
    const script = document.createElement('script');
    script.innerHTML = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init("phc_yHzTTwYMUQkr2NEKYqtRw394w8PJpV7A5CpKotGQKfVG",{api_host:"https://us.i.posthog.com",person_profiles:"always",session_recording:{recordCrossOriginIframes:true}});
    `;
    document.head.appendChild(script);
  }, []);
  
  // TrustedForm cert URL - can come from URL param or postMessage from parent
  const [trustedFormCertUrl, setTrustedFormCertUrl] = useState(
    () => searchParams.get('trustedform_cert_url') || null
  );
  
  // Funnel compliance settings (MA-OPS-061) - loaded from funnel data
  const [complianceSettings, setComplianceSettings] = useState(null);
  
  // TrustedForm runtime integration (MA-OPS-061)
  // Loads TrustedForm script inside widget when compliance settings enable it
  const trustedFormRuntime = useTrustedFormRuntime(
    complianceSettings,
    trustedFormCertUrl
  );

  // --------------------------------------------------------------------------
  // Height Communication (Critical for GHL & Safari)
  // MUST be defined before useEffects that use it
  // --------------------------------------------------------------------------
  
  const sendHeight = useCallback(() => {
    if (!isEmbed || !containerRef.current) return;
    
    // Get the container's actual content height
    const containerHeight = containerRef.current.scrollHeight;
    const offsetHeight = containerRef.current.offsetHeight;
    
    // Check if we're in booking state (no minimum height constraint)
    const funnelElement = containerRef.current.querySelector('[data-funnel-state="booking"]');
    const isBookingState = !!funnelElement;
    
    // Dynamic minimum based on funnel state
    // Chat mode: Min 310px (header 56 + thread 110 + response 130 + padding)
    // Booking mode: No minimum - use actual content height
    const MIN_HEIGHT = isBookingState ? 0 : 310;
    const MAX_HEIGHT = 900;
    const height = Math.min(Math.max(containerHeight, offsetHeight, MIN_HEIGHT), MAX_HEIGHT);
    
    // Only send if changed significantly (prevent jitter)
    if (Math.abs(height - lastHeightRef.current) > 5) {
      lastHeightRef.current = height;
      postToParent('FUNNEL_RESIZE', { height });
    }
  }, [isEmbed]);

  // --------------------------------------------------------------------------
  // Listen for messages from parent page and booking widget
  // --------------------------------------------------------------------------
  
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || !data.type) return;
      
      // Receive TrustedForm cert from parent
      if (data.type === 'MISSIONASSIST_TRUSTEDFORM_CERT' && data.payload?.certUrl) {
        console.log('[MissionAssist] Received TrustedForm cert from parent:', data.payload.certUrl);
        setTrustedFormCertUrl(data.payload.certUrl);
      }
      
      // Parent requesting height
      if (data.type === 'MISSIONASSIST_REQUEST_HEIGHT') {
        sendHeight();
      }
      
      // Forward WIDGET_HEIGHT from booking widget to parent
      // This handles the case when QuickBookingWidget posts height updates
      if (data.type === 'WIDGET_HEIGHT' || data.type === 'setHeight') {
        const height = data.payload?.height || data.height;
        if (height && isEmbed) {
          postToParent('FUNNEL_RESIZE', { height });
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Request TrustedForm cert from parent on load
    if (isEmbed) {
      postToParent('MISSIONASSIST_REQUEST_TRUSTEDFORM');
    }
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isEmbed, sendHeight]);

  // --------------------------------------------------------------------------
  // Background & Layout Setup - CRITICAL for embed mode
  // --------------------------------------------------------------------------
  
  useEffect(() => {
    // Force transparent background for embed mode
    if (isEmbed) {
      // Add embed-mode class for CSS override
      document.body.classList.add('embed-mode');
      document.body.classList.remove('dark');
      document.documentElement.classList.remove('dark');
      
      // Override ALL possible background sources with inline styles
      const transparentStyle = 'background: transparent !important; background-color: transparent !important; min-height: auto !important;';
      
      document.documentElement.style.cssText = transparentStyle;
      document.body.style.cssText = transparentStyle + ' margin: 0; padding: 0; overflow-x: hidden;';
      
      // Target #root container
      const root = document.getElementById('root');
      if (root) {
        root.style.cssText = transparentStyle;
        
        // Also target the first child div (App wrapper with bg-app class)
        const appWrapper = root.querySelector('[data-testid="app-root"]') || root.firstElementChild;
        if (appWrapper) {
          appWrapper.style.cssText = transparentStyle;
        }
      }
      
      // Use MutationObserver to catch any dynamically added wrappers
      const observer = new MutationObserver(() => {
        const appRoot = document.querySelector('[data-testid="app-root"]');
        if (appRoot) {
          appRoot.style.cssText = transparentStyle;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      
      return () => observer.disconnect();
    } else {
      // Non-embed mode - use light gray
      document.body.classList.remove('embed-mode');
      document.body.style.background = '#E8E8ED';
      document.body.style.backgroundColor = '#E8E8ED';
      document.documentElement.style.background = '#E8E8ED';
      document.documentElement.style.backgroundColor = '#E8E8ED';
      document.body.classList.remove('dark');
      document.body.style.minHeight = '100vh';
      document.documentElement.style.minHeight = '100vh';
      // Override the app root bg-app gradient
      const appRoot = document.querySelector('[data-testid="app-root"]');
      if (appRoot) {
        appRoot.style.background = '#E8E8ED';
        appRoot.style.backgroundColor = '#E8E8ED';
      }
    }
  }, [isEmbed]);

  // --------------------------------------------------------------------------
  // Height Observers and Periodic Checks
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isEmbed) return;

    // Initial height send after content renders
    const initialTimer = setTimeout(sendHeight, 300);
    
    // ResizeObserver for content changes - only observe container, not body
    if (window.ResizeObserver && containerRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        sendHeight();
      });
      resizeObserverRef.current.observe(containerRef.current);
    }
    
    // Periodic height check (Safari/GHL compatibility) - less frequent
    heightCheckIntervalRef.current = setInterval(sendHeight, 1000);
    
    // Window resize handler
    window.addEventListener('resize', sendHeight);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(heightCheckIntervalRef.current);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', sendHeight);
    };
  }, [isEmbed, sendHeight]);

  // --------------------------------------------------------------------------
  // Configuration
  // --------------------------------------------------------------------------
  
  const tenantId = searchParams.get('tenant_id');
  const funnelSlug = searchParams.get('funnel');
  const embedKey = searchParams.get('embed_key');  // MA-TENANT-001: Preferred secure identifier
  const testMode = searchParams.get('test_mode') === 'true';
  const lpTheme = searchParams.get('theme');  // MA-ARCH-186-P0-FIX: 'lp_dark' from LP iframe
  
  // MA-ARCH-183B: Derive brand config from URL params
  // Only set brandConfig when explicit brand_accent is provided.
  // theme=lp_dark alone should NOT create a brandConfig — the funnel
  // should keep its own configured appearance colors; lpTheme forces surfaceMode separately.
  const hasExplicitBrand = searchParams.get('brand_accent');
  const [sessionBrandConfig, setSessionBrandConfig] = useState(null);
  const brandConfig = useMemo(() => {
    if (hasExplicitBrand) return deriveBrandFromParams(searchParams, null);
    return sessionBrandConfig;
  }, [searchParams, hasExplicitBrand, sessionBrandConfig]);

  // Prefill data from URL params
  const prefillData = useMemo(() => ({
    first_name: searchParams.get('prefill_first_name') || '',
    last_name: searchParams.get('prefill_last_name') || '',
    email: searchParams.get('prefill_email') || '',
    phone: searchParams.get('prefill_phone') || '',
    contact_id: searchParams.get('prefill_contact_id') || ''
  }), [searchParams]);

  // Attribution - includes parent page context
  const attribution = useMemo(() => ({
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
    // Parent page URL (passed from embed.js)
    page_url: searchParams.get('page_url') || window.location.href,
    referrer_url: searchParams.get('referrer_url') || document.referrer,
    // Legacy field names for backwards compatibility
    referrer: searchParams.get('referrer_url') || document.referrer,
    landing_page: searchParams.get('page_url') || window.location.href,
    // Click IDs
    fbclid: searchParams.get('fbclid'),
    gclid: searchParams.get('gclid'),
    msclkid: searchParams.get('msclkid'),
    ttclid: searchParams.get('ttclid'),
    // Embed context
    embed_id: embedId,
    embed_version: searchParams.get('embed_version'),
    // MA-CAMPAIGNS-003: Campaign ID for attribution
    campaign_id: searchParams.get('campaign_id')
  }), [searchParams, embedId]);
  
  // MA-CAMPAIGNS-003: Extract campaign_id for explicit session attribution
  const campaignId = searchParams.get('campaign_id');

  // --------------------------------------------------------------------------
  // Event Handlers
  // --------------------------------------------------------------------------
  
  const handleStepViewed = useCallback((stepData) => {
    postToParent('FUNNEL_STEP_VIEWED', {
      ...stepData,
      timestamp: new Date().toISOString()
    });
    // Trigger height update after step change
    setTimeout(sendHeight, 100);
  }, [sendHeight]);

  const handleStepCompleted = useCallback((stepData) => {
    postToParent('FUNNEL_STEP_COMPLETED', {
      ...stepData,
      timestamp: new Date().toISOString()
    });
    setTimeout(sendHeight, 100);
  }, [sendHeight]);

  const handleConsentGiven = useCallback((consentData) => {
    postToParent('FUNNEL_CONSENT_GIVEN', {
      ...consentData,
      timestamp: new Date().toISOString()
    });
    setTimeout(sendHeight, 100);
  }, [sendHeight]);

  const handleBookingStart = useCallback((config) => {
    postToParent('FUNNEL_BOOKING_STARTED', { 
      contact: config,
      timestamp: new Date().toISOString()
    });
    setTimeout(sendHeight, 100);
  }, [sendHeight]);

  const handleFunnelComplete = useCallback((result) => {
    postToParent('FUNNEL_COMPLETE', {
      ...result,
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleBookingSuccess = useCallback((result) => {
    postToParent('BOOKING_SUCCESS', {
      ...result,
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleError = useCallback((error) => {
    postToParent('FUNNEL_ERROR', { 
      message: error?.message || 'Unknown error',
      timestamp: new Date().toISOString()
    });
  }, []);

  // --------------------------------------------------------------------------
  // Validation
  // --------------------------------------------------------------------------
  
  // MA-TENANT-001: Require either embed_key or funnel_slug
  if (!funnelSlug && !embedKey) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: isEmbed ? 'transparent' : '#E8E8ED' }}
      >
        <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-6 max-w-sm text-center">
          <p className="text-[#FF3B30] font-medium text-sm mb-1">Configuration Missing</p>
          <p className="text-[#8E8E93] text-xs">
            Required parameter: embed_key or funnel
          </p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  
  return (
    <div 
      ref={containerRef}
      className={isEmbed ? '' : 'py-8 px-4'}
      style={{ 
        backgroundColor: 'transparent',
        background: 'transparent',
        minHeight: isEmbed ? 'auto' : 'auto',
        padding: isEmbed ? '8px' : undefined
      }}
    >
      <div className={isEmbed ? '' : 'max-w-lg mx-auto'}>
        <ConversationFunnel
          tenantId={tenantId}
          funnelSlug={funnelSlug}
          embedKey={embedKey}
          attribution={attribution}
          prefillData={prefillData}
          externalTrustedFormCertUrl={trustedFormRuntime.certUrl}
          testMode={testMode}
          lpTheme={lpTheme}
          brandConfig={brandConfig}
          onBookingStart={handleBookingStart}
          onComplete={handleFunnelComplete}
          onStepViewed={handleStepViewed}
          onStepCompleted={handleStepCompleted}
          onConsentGiven={handleConsentGiven}
          onBookingSuccess={handleBookingSuccess}
          onError={handleError}
          onFunnelLoaded={(funnelData) => {
            // MA-THEME-PHASE4: Use brand_appearance from session for inherit_brand funnels
            if (funnelData?.brand_appearance && !hasExplicitBrand) {
              setSessionBrandConfig(funnelData.brand_appearance);
            }
            // MA-OPS-061: Load compliance settings from funnel
            if (funnelData?.settings?.compliance) {
              setComplianceSettings(funnelData.settings.compliance);
              console.log('[TrustedForm] Loaded compliance settings from funnel:', funnelData.settings.compliance);
            }
          }}
          trustedFormRuntime={trustedFormRuntime}
        />
      </div>
    </div>
  );
}
